<template>
  <div class="flex flex-col gap-3">
    <CSearch
      v-if="launchDarkly.enable_reorder_search"
      v-model="searchTerm"
      :placeholder="t('canvasUI.canvasBuilder.searchPlaceholder')"
      show-icon
      size="small"
    />

    <Draggable
      v-model="activeChildren"
      class="flex flex-col gap-10px overflow-auto"
      force-fallback
      handle=".c-component-list-item__drag-handle"
      itemKey="id"
    >
      <CComponentListItem
        v-for="element in filteredChildren"
        :key="element.id"
        clickable
        :component="element"
        :is-clicked="isComponentIsolated(element.id)"
        :is-home="isHomePage(element.id)"
        removable
        reorderable
        :show-component-manager="launchDarkly.enable_reorder_component_options_menu"
        :title="getTitle(element)"
        :type="element.type"
        @displayChildren="setActiveComponentId(element.id)"
        @removeComponent="removeComponent"
        @renameComponent="renameComponent"
        @selectComponent="selectComponent(element)"
      />
    </Draggable>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable as Draggable } from 'vue-draggable-plus'

import CComponentListItem from '@lib/components/CComponentListItem/CComponentListItem.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import useReorderComponent from '@canvas-builder/composables/useReorderComponent'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { getNodeDisplayNameByComponentType } from '@canvas-builder/util/canvas.util'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const { t } = useI18n()
const {
  getTitle,
  setActiveComponentId,
  selectComponent,
  removeComponent,
  renameComponent,
  activeChildren,
  isComponentIsolated,
} = useReorderComponent()
const { canvasHomePage } = useCanvas()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const searchTerm = ref('')

const filteredChildren = computed(() => {
  if (!launchDarkly.value.enable_reorder_search || !searchTerm.value.trim()) {
    return activeChildren.value
  }

  const search = searchTerm.value.toLowerCase().trim()
  return activeChildren.value.filter((element) => {
    const displayName = getNodeDisplayNameByComponentType(element).toLowerCase()
    return displayName.includes(search)
  })
})

// Determine if a component is set as home page
const isHomePage = (componentId: string) => {
  return canvasHomePage.value?.component_id === componentId
}
</script>
